import { useState } from 'react'
import { Outlet, Navigate, NavLink, Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { 
  ShieldAlert, Users, Building2, Activity, Settings, List, X, Menu, LogOut, FileText
} from 'lucide-react'
import clsx from 'clsx'

export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, isAuthenticated, isLoading, logout } = useAuth()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-950">
        <div className="w-8 h-8 border-4 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />
  }

  // RBAC protection check
  if (user.platformRole !== 'super_admin' && user.platformRole !== 'platform_admin' && user.platformRole !== 'support_admin') {
    // Regular users redirected to normal dashboard
    return <Navigate to="/dashboard" replace />
  }

  const navItems = [
    { label: 'Overview', href: '/admin', icon: Activity, exact: true },
    { label: 'Users', href: '/admin/users', icon: Users },
    { label: 'Businesses', href: '/admin/businesses', icon: Building2 },
    { label: 'Audit Logs', href: '/admin/audit-logs', icon: List },
    { label: 'Settings', href: '/admin/settings', icon: Settings },
  ]

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden text-slate-200">
      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed top-0 left-0 h-full z-30 w-[240px] flex flex-col',
          'bg-slate-900 border-r border-rose-900/30',
          'transition-transform duration-250 ease-out',
          'lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo area */}
        <div className="flex items-center justify-between px-4 h-16 border-b border-rose-900/30 shrink-0 bg-rose-950/20">
          <div className="flex items-center gap-2.5">
            <ShieldAlert size={20} className="text-rose-500" />
            <div>
              <p className="text-xs font-bold text-rose-100 uppercase tracking-wider">Super Admin</p>
              <p className="text-[10px] text-rose-400">Control Panel</p>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1.5 text-slate-400">
            <X size={16} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
          <p className="px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Platform Management</p>
          {navItems.map(item => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.exact}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive ? 'bg-rose-500/10 text-rose-400' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                )
              }
            >
              <item.icon size={16} />
              {item.label}
            </NavLink>
          ))}
          
          <div className="mt-8">
            <p className="px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">User App</p>
            <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors">
              <LogOut size={16} className="rotate-180" />
              Exit Admin Panel
            </Link>
          </div>
        </nav>
      </aside>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-[240px]">
        {/* Header */}
        <header className="h-16 border-b border-rose-900/30 bg-slate-900/50 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-10 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 -ml-2 text-slate-400">
              <Menu size={20} />
            </button>
            <span className="text-sm font-medium text-rose-400 border border-rose-500/20 bg-rose-500/10 px-2 py-0.5 rounded flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></span>
              Restricted Area
            </span>
          </div>
          <div className="text-sm font-medium text-slate-300">
            {user.name} ({user.platformRole})
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="max-w-6xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
